import type { Asset } from '@compass/client/types/asset';
import { AssetType } from '@compass/client/types/asset';

import { apiClient, FILE_UPLOAD_TIMEOUT } from './api.config';

/**
 * Asset service. Deliberately still on `apiClient` rather than the generated
 * `assetService` or the shared `transport` (BV-1874). Two hard blockers, both verified
 * against the backend:
 *
 *  1. **`asset_type` is a string on the wire, not a number.**
 *     `server/http/api/assets_handlers.go` parses `asset_type` with a string switch
 *     (`"call_recording"` / `"ASSET_TYPE_CALL_RECORDING"`) and returns **400** on anything
 *     it doesn't recognise. The generated service forwards the numeric proto enum, which
 *     that switch rejects — so delegating would turn every filtered list into a 400. The
 *     enum→string map below is what keeps it working.
 *
 *  2. **The download paths need response headers.** They fall back to
 *     `response.headers['content-type']` when a blob has no `type`, and the `Transport`
 *     contract returns only the unwrapped body. Routing them through the transport would
 *     mean either losing that fallback or widening `Transport` with a response-envelope
 *     variant, which would complicate it for every other consumer.
 *
 * Note also that `ASSET_TYPE_USER_ATTACHMENT` appears in the maps below but NOT in the
 * handler's switch, so filtering by it 400s. Pre-existing; out of scope here.
 */

// Valid content types for different asset types
const VALID_AUDIO_TYPES = [
  'audio/mpeg',
  'audio/mp3',
  'audio/wav',
  'audio/wave',
  'audio/ogg',
  'audio/webm',
  'audio/aac',
  'audio/mp4',
  'audio/x-m4a',
  'application/octet-stream', // Some servers return this for audio
];

const VALID_TRANSCRIPT_TYPES = [
  'application/json',
  'text/json',
  'application/x-ndjson',
  'text/plain',
  'application/octet-stream',
];

const VALID_PDF_TYPES = ['application/pdf'];

interface CaseRecordContext {
  organizationId: string;
  programId: string;
  caseRecordId: string;
}

class AssetsService {
  /**
   * List assets for a case record with optional call_id filter
   */
  async listAssets(
    context: CaseRecordContext,
    callId?: string,
    assetType?: AssetType,
  ): Promise<Asset[]> {
    const { organizationId, programId, caseRecordId } = context;

    // Build query parameters
    const params = new URLSearchParams();
    if (callId) {
      params.append('call_id', callId);
    }

    if (assetType) {
      // Convert enum to string value if needed
      const assetTypeMap: Record<AssetType, string> = {
        [AssetType.ASSET_TYPE_UNSPECIFIED]: '',
        [AssetType.ASSET_TYPE_CALL_TRANSCRIPT]: 'call_transcript',
        [AssetType.ASSET_TYPE_CALL_RECORDING]: 'call_recording',
        [AssetType.ASSET_TYPE_CALL_FULL_RECORDING]: 'call_full_recording',
        [AssetType.ASSET_TYPE_CALL_DOCUMENT]: 'call_document',
        [AssetType.ASSET_TYPE_FAX]: 'fax',
        [AssetType.ASSET_TYPE_USER_ATTACHMENT]: 'user_attachment',
      };
      const assetTypeString = assetTypeMap[assetType];
      if (assetTypeString) {
        params.append('asset_type', assetTypeString);
      }
    }

    // Use the new endpoint format with path parameters
    const queryString = params.toString();
    const url = `/organizations/${organizationId}/programs/${programId}/caserecords/${caseRecordId}/assets${queryString ? `?${queryString}` : ''}`;

    // The API returns an object with assets array
    const response = await apiClient.get<{ assets: Asset[] }>(url);
    return response.data?.assets || [];
  }

  /**
   * @deprecated Use listAssets with CaseRecordContext instead
   * List assets by call_id only (uses deprecated route)
   */
  async listAssetsByCallId(callId: string, assetType?: AssetType): Promise<Asset[]> {
    // Build query parameters
    const params = new URLSearchParams();
    params.append('call_id', callId);

    if (assetType) {
      // Convert enum to string value if needed
      const assetTypeMap: Record<AssetType, string> = {
        [AssetType.ASSET_TYPE_UNSPECIFIED]: '',
        [AssetType.ASSET_TYPE_CALL_TRANSCRIPT]: 'call_transcript',
        [AssetType.ASSET_TYPE_CALL_RECORDING]: 'call_recording',
        [AssetType.ASSET_TYPE_CALL_FULL_RECORDING]: 'call_full_recording',
        [AssetType.ASSET_TYPE_CALL_DOCUMENT]: 'call_document',
        [AssetType.ASSET_TYPE_FAX]: 'fax',
        [AssetType.ASSET_TYPE_USER_ATTACHMENT]: 'user_attachment',
      };
      const assetTypeString = assetTypeMap[assetType];
      if (assetTypeString) {
        params.append('asset_type', assetTypeString);
      }
    }

    // Use the deprecated endpoint format with query parameters
    const url = `/assets/list?${params.toString()}`;

    // The API returns an array directly for deprecated route
    const response = await apiClient.get<Asset[]>(url);
    return response.data || [];
  }

  /**
   * @deprecated Use listAssets with CaseRecordContext instead
   * List assets by case record ID only (uses deprecated route)
   */
  async listAssetsByCaseRecord(caseRecordId: string, assetType?: AssetType): Promise<Asset[]> {
    // Build query parameters
    const params = new URLSearchParams();
    params.append('case_record_id', caseRecordId);

    if (assetType) {
      // Convert enum to string value if needed
      const assetTypeMap: Record<AssetType, string> = {
        [AssetType.ASSET_TYPE_UNSPECIFIED]: '',
        [AssetType.ASSET_TYPE_CALL_TRANSCRIPT]: 'call_transcript',
        [AssetType.ASSET_TYPE_CALL_RECORDING]: 'call_recording',
        [AssetType.ASSET_TYPE_CALL_FULL_RECORDING]: 'call_full_recording',
        [AssetType.ASSET_TYPE_CALL_DOCUMENT]: 'call_document',
        [AssetType.ASSET_TYPE_FAX]: 'fax',
        [AssetType.ASSET_TYPE_USER_ATTACHMENT]: 'user_attachment',
      };
      const assetTypeString = assetTypeMap[assetType];
      if (assetTypeString) {
        params.append('asset_type', assetTypeString);
      }
    }

    // Use the deprecated endpoint format with query parameters
    const url = `/assets/list?${params.toString()}`;

    // The API returns an array directly for deprecated route
    const response = await apiClient.get<Asset[]>(url);
    return response.data || [];
  }

  /**
   * Validate blob response
   */
  private validateBlobResponse(data: any): asserts data is Blob {
    if (!data) {
      throw new Error('No data received from server');
    }

    if (!(data instanceof Blob)) {
      throw new Error('Invalid response type - expected Blob');
    }

    if (data.size === 0) {
      throw new Error('Received empty file from server');
    }
  }

  /**
   * Verify content type for specific asset types
   */
  private verifyContentType(
    contentType: string,
    expectedType?: 'audio' | 'transcript' | 'pdf',
  ): void {
    // Check for error responses
    if (contentType.includes('text/html')) {
      console.error('Received HTML response instead of expected file');
      throw new Error('Server returned an error page instead of the requested file');
    }

    if (!expectedType || !contentType) {
      return;
    }

    // Verify audio content type
    if (expectedType === 'audio' && !VALID_AUDIO_TYPES.some(type => contentType.includes(type))) {
      console.warn(`Unexpected content type for audio file: ${contentType}`);
    }

    // Verify transcript content type
    if (
      expectedType === 'transcript' &&
      !VALID_TRANSCRIPT_TYPES.some(type => contentType.includes(type))
    ) {
      console.warn(`Unexpected content type for transcript file: ${contentType}`);
    }

    // Verify PDF content type
    if (expectedType === 'pdf' && !VALID_PDF_TYPES.some(type => contentType.includes(type))) {
      console.warn(`Unexpected content type for PDF file: ${contentType}`);
    }
  }

  /**
   * Download an asset using the new path format
   * Includes content-type verification for audio files
   */

  async downloadAsset(
    context: CaseRecordContext,
    assetId: string,
    expectedType?: 'audio' | 'transcript' | 'pdf',
  ): Promise<Blob> {
    if (!assetId || typeof assetId !== 'string') {
      throw new Error('Invalid asset ID provided');
    }

    const { organizationId, programId, caseRecordId } = context;

    // Build request config with optional custom auth
    const config: any = {
      responseType: 'blob',
      timeout: FILE_UPLOAD_TIMEOUT,
    };

    const response = await apiClient.get(
      `/organizations/${organizationId}/programs/${programId}/caserecords/${caseRecordId}/assets/${assetId}/download`,
      config,
    );

    // Validate blob response
    this.validateBlobResponse(response.data);

    // Get content type from response
    const contentType = response.data.type || (response.headers?.['content-type'] as string) || '';

    // Verify content type if specified
    this.verifyContentType(contentType, expectedType);

    return response.data;
  }

  /**
   * @deprecated Use downloadAsset with CaseRecordContext instead
   * Download an asset using the deprecated route
   */
  async downloadAssetById(assetId: string, expectedType?: 'audio' | 'transcript'): Promise<Blob> {
    if (!assetId || typeof assetId !== 'string') {
      throw new Error('Invalid asset ID provided');
    }

    // Use the deprecated endpoint format
    const response = await apiClient.get(`/assets/${assetId}/download`, {
      responseType: 'blob',
      timeout: FILE_UPLOAD_TIMEOUT,
    });

    // Validate blob response
    this.validateBlobResponse(response.data);

    // Get content type from response
    const contentType = response.data.type || (response.headers?.['content-type'] as string) || '';

    // Verify content type if specified
    this.verifyContentType(contentType, expectedType);

    return response.data;
  }

  /**
   * Download an asset from Arcus (for recordings without Twilio SID)
   * Used when call.sid is missing (direct uploads via API)
   */
  async downloadAssetFromArcus(
    context: CaseRecordContext,
    assetId: string,
    expectedType?: 'audio' | 'transcript' | 'pdf',
  ): Promise<Blob> {
    if (!assetId || typeof assetId !== 'string') {
      throw new Error('Invalid asset ID provided');
    }

    const { organizationId, programId, caseRecordId } = context;

    // Use the new Arcus-specific endpoint with path parameters
    const response = await apiClient.get(
      `/organizations/${organizationId}/programs/${programId}/caserecords/${caseRecordId}/assets/${assetId}/download-from-arcus`,
      {
        responseType: 'blob',
        timeout: FILE_UPLOAD_TIMEOUT,
      },
    );

    // Validate blob response
    this.validateBlobResponse(response.data);

    // Get content type from response
    const contentType = response.data.type || (response.headers?.['content-type'] as string) || '';

    // Verify content type if specified
    this.verifyContentType(contentType, expectedType);

    return response.data;
  }

  /**
   * @deprecated Use downloadAssetFromArcus with CaseRecordContext instead
   * Download an asset from Arcus using the deprecated route
   */
  async downloadAssetFromArcusById(
    assetId: string,
    expectedType?: 'audio' | 'transcript',
  ): Promise<Blob> {
    if (!assetId || typeof assetId !== 'string') {
      throw new Error('Invalid asset ID provided');
    }

    // Use the deprecated Arcus endpoint
    const response = await apiClient.get(`/assets/${assetId}/download-from-arcus`, {
      responseType: 'blob',
      timeout: FILE_UPLOAD_TIMEOUT,
    });

    // Validate blob response
    this.validateBlobResponse(response.data);

    // Get content type from response
    const contentType = response.data.type || (response.headers?.['content-type'] as string) || '';

    // Verify content type if specified
    this.verifyContentType(contentType, expectedType);

    return response.data;
  }

  /**
   * Download an asset with range support for incremental fetching
   * Used for streaming transcripts or large files
   * @param context - Case record context with org, program, and case record IDs
   * @param assetId - Asset ID to download
   * @param rangeStart - Byte offset to start from
   * @param rangeEnd - Optional byte offset to end at (omit for "to end of file")
   * @returns Response with data and headers
   */
  async downloadAssetWithRange(
    context: CaseRecordContext,
    assetId: string,
    rangeStart: number,
    rangeEnd?: number,
  ): Promise<{ data: string; headers: any; status: number }> {
    if (!assetId || typeof assetId !== 'string') {
      throw new Error('Invalid asset ID provided');
    }

    const { organizationId, programId, caseRecordId } = context;

    // Build range header
    const rangeHeader =
      rangeEnd !== undefined ? `bytes=${rangeStart}-${rangeEnd}` : `bytes=${rangeStart}-`;

    // Make request with range header
    const response = await apiClient.get(
      `/organizations/${organizationId}/programs/${programId}/caserecords/${caseRecordId}/assets/${assetId}/download`,
      {
        headers: {
          Range: rangeHeader,
        },
        responseType: 'text', // Get as text for transcript parsing
        timeout: FILE_UPLOAD_TIMEOUT,
        validateStatus: status => status === 200 || status === 206, // Accept both full and partial content
      },
    );

    return {
      data: response.data,
      headers: response.headers,
      status: response.status,
    };
  }

  /**
   * @deprecated Use downloadAssetWithRange with CaseRecordContext instead
   * Download an asset with range support using the deprecated route
   */
  async downloadAssetWithRangeById(
    assetId: string,
    rangeStart: number,
    rangeEnd?: number,
  ): Promise<{ data: string; headers: any; status: number }> {
    if (!assetId || typeof assetId !== 'string') {
      throw new Error('Invalid asset ID provided');
    }

    // Build range header
    const rangeHeader =
      rangeEnd !== undefined ? `bytes=${rangeStart}-${rangeEnd}` : `bytes=${rangeStart}-`;

    // Make request with range header using deprecated endpoint
    const response = await apiClient.get(`/assets/${assetId}/download`, {
      headers: {
        Range: rangeHeader,
      },
      responseType: 'text', // Get as text for transcript parsing
      timeout: FILE_UPLOAD_TIMEOUT,
      validateStatus: status => status === 200 || status === 206, // Accept both full and partial content
    });

    return {
      data: response.data,
      headers: response.headers,
      status: response.status,
    };
  }
}

export const assetsService = new AssetsService();
